import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useGetServicesQuery,
  useDeleteServiceMutation,
} from "../redux/services/api";
import { selectIsAuthenticated } from "../redux/slices/authSlice";
import Icon from "../components/Icon";
import { createExcerpt } from "../utils/excerpt";
import { useTranslation } from "../hooks/useTranslation";
import StatusMessage from "../components/StatusMessage";
import { useMessageType } from "../hooks/useMessageType";

const Services = () => {
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { t } = useTranslation();

  // RTK Query hooks
  const { data: servicesData, isLoading: loading } = useGetServicesQuery();
  const [deleteService] = useDeleteServiceMutation();

  const services = servicesData?.services || [];
  const [statusMessage, setStatusMessage] = useState("");
  const messageType = useMessageType(statusMessage);

  const handleDelete = async (id) => {
    if (!window.confirm(t("servicesMainDashboard.deleteConfirmMessage"))) {
      return;
    }

    try {
      await deleteService(id).unwrap();
      setStatusMessage(t("servicesMainDashboard.serviceDeletedSuccessfully"));
    } catch (error) {
      console.error("Error deleting service:", error);
      setStatusMessage(
        error.data?.message || t("servicesMainDashboard.errorDeletingService")
      );
    }
  };

  const handleAddNew = () => {
    navigate("/dashboard/services/add");
  };

  const handleEdit = (serviceId) => {
    navigate(`/dashboard/services/edit/${serviceId}`);
  };

  const handleServiceClick = (serviceId) => {
    navigate(`/services/${serviceId}`);
  };

  if (loading) {
    return (
      <div className="services">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t("servicesMainDashboard.loading")}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="services">
        <div className="auth-required">
          <h2>{t("servicesMainDashboard.authenticationRequired")}</h2>
          <p>{t("servicesMainDashboard.pleaseLoginToManage")}</p>
          <button className="login-btn" onClick={() => navigate("/login")}>
            {t("servicesMainDashboard.goToLogin")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="services">
      <div className="page-header">
        <div className="header-content">
          <h1>{t("servicesMainDashboard.services")}</h1>
          <p>{t("servicesMainDashboard.manageYourServices")}</p>
        </div>
        <div className="header-actions">
          <button type="button" className="add-btn" onClick={handleAddNew}>
            <Icon name="plus" size="16px" />
            {t("servicesMainDashboard.addNewService")}
          </button>
        </div>
      </div>

      <StatusMessage
        message={statusMessage}
        type={messageType}
        show={!!statusMessage}
        autoClose={messageType === "success"}
        autoCloseDelay={5000}
        onClose={() => setStatusMessage("")}
      />

      <div className="services-container">
        {services.length > 0 ? (
          <div className="services-table-container">
            <table className="services-table">
              <thead>
                <tr>
                  <th>{t("servicesMainDashboard.image")}</th>
                  <th>{t("servicesMainDashboard.title")}</th>
                  <th>{t("servicesMainDashboard.category")}</th>
                  <th>{t("servicesMainDashboard.contentPreview")}</th>
                  <th>{t("servicesMainDashboard.status")}</th>
                  <th>{t("servicesMainDashboard.created")}</th>
                  <th>{t("servicesMainDashboard.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {services.map((service) => (
                  <tr
                    key={service.id}
                    className="service-row"
                    onClick={() => handleServiceClick(service.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <td className="image-cell">
                      {service.image_url ? (
                        <img
                          src={service.image_url}
                          alt={service.title}
                          className="service-thumbnail"
                        />
                      ) : (
                        <div className="no-image">
                          <Icon name="image" size="24px" />
                        </div>
                      )}
                    </td>
                    <td className="title-cell">{service.title}</td>
                    <td className="category-cell">
                      <span className="category-badge">
                        {service.category?.title ||
                          t("servicesMainDashboard.noCategory")}
                      </span>
                    </td>
                    <td className="content-cell">
                      <div className="content-preview">
                        {createExcerpt(service.content, 15)}
                      </div>
                    </td>
                    <td className="status-cell">
                      <span
                        className={`status-badge ${
                          service.is_active ? "active" : "inactive"
                        }`}
                      >
                        {service.is_active
                          ? t("servicesMainDashboard.active")
                          : t("servicesMainDashboard.inactive")}
                      </span>
                    </td>
                    <td className="date-cell">
                      {new Date(service.created_at).toLocaleDateString()}
                    </td>
                    <td className="actions-cell">
                      <div
                        className="action-buttons"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="edit-btn"
                          onClick={() => handleEdit(service.id)}
                          title={t("servicesMainDashboard.editService")}
                        >
                          <Icon name="edit" size="14px" />
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(service.id)}
                          title={t("servicesMainDashboard.deleteService")}
                        >
                          <Icon name="trash" size="14px" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-services">
            <div className="empty-content">
              <Icon name="briefcase" size="48px" />
              <h3>No Services Yet</h3>
              <p>Get started by creating your first service.</p>
              <button className="add-first-service-btn" onClick={handleAddNew}>
                <Icon name="plus" size="16px" />
                Add First Service
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Services;
